import React, { useState, useContext, useRef, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  ProgressBar,
} from "react-bootstrap";
import { Editor, EditorState, RichUtils } from "draft-js";
import "draft-js/dist/Draft.css";

import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";

import { BiEdit } from "react-icons/bi";
import { AiOutlineFileDone } from "react-icons/ai";
import { BsArrowLeft } from "react-icons/bs";

import { Link, useNavigate } from "react-router-dom";
import { BoletimContext } from "../../../context/BoletimContext";
import CabecalhoBoletim from "../components/CabecalhoBoletim";
import { sanitizeHistoryHtml, hasUnsupportedHistory } from "../../../Utils/historyHtml";
import "./historico.css";


const Historico = () => {
  const { boletim, setBoletim } = useContext(BoletimContext);
  const [editorState, setEditorState] = useState(() =>
    boletim.historicohtml
      ? EditorState.createWithContent(stateFromHTML(sanitizeHistoryHtml(boletim.historicohtml)))
      : EditorState.createEmpty()
  );

  const editorRef = useRef(null);
  const contentRef = useRef(editorState.getCurrentContent());
  const lastHtmlRef = useRef(boletim.historicohtml || '');
  const [expanded, setExpanded] = useState(false);
  const [needsConversion, setNeedsConversion] = useState(() => hasUnsupportedHistory(boletim.historicohtml));
  const shellRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const html = boletim.historicohtml || '';
    if (html === lastHtmlRef.current) return;
    lastHtmlRef.current = html;
    const next = EditorState.createWithContent(stateFromHTML(sanitizeHistoryHtml(html)));
    contentRef.current = next.getCurrentContent();
    setEditorState(next);
    setNeedsConversion(hasUnsupportedHistory(html));
  }, [boletim.historicohtml]);

  useEffect(() => {
    if (!expanded) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const resize = () => {
      const viewport = window.visualViewport;
      shellRef.current?.style.setProperty('--editor-height', `${viewport?.height || window.innerHeight}px`);
      shellRef.current?.style.setProperty('--editor-top', `${viewport?.offsetTop || 0}px`);
    };
    const escape = event => { if (event.key === 'Escape') setExpanded(false); };
    resize();
    window.visualViewport?.addEventListener('resize', resize);
    window.visualViewport?.addEventListener('scroll', resize);
    window.addEventListener('keydown', escape);
    return () => {
      document.body.style.overflow = previous;
      window.visualViewport?.removeEventListener('resize', resize);
      window.visualViewport?.removeEventListener('scroll', resize);
      window.removeEventListener('keydown', escape);
    };
  }, [expanded]);

  const handleHistoricoChange = (state) => {
    setEditorState(state);
    if (state.getCurrentContent() === contentRef.current) return;
    contentRef.current = state.getCurrentContent();
    const html = stateToHTML(state.getCurrentContent());
    lastHtmlRef.current = html;
    setBoletim((current) => ({ ...current, historicohtml: html }));
  };

  const tool = (label, action, active = false, disabled = false) => (
    <button type="button" aria-label={label} aria-pressed={active} title={label}
      disabled={disabled || needsConversion} onMouseDown={event => event.preventDefault()}
      onClick={() => { action(); editorRef.current?.focus(); }}>{label}</button>
  );

  const handleSalvarHistorico = () => {
    navigate("../VerBoletim");
  };



  return (
    <div className="bo-form-root">
      <CabecalhoBoletim texto="Histórico da Ocorrência" />

      <ProgressBar now={100} className="bo-progress" />

      <div ref={shellRef} className={`bo-section history-shell${expanded ? ' history-expanded' : ''}`}>
        <div className="bo-section-title">
          <BiEdit /> Relato do Fato
        </div>

        <button type="button" className="history-expand" aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}>{expanded ? 'Voltar ao formulário' : 'Expandir editor'}</button>
        {needsConversion && <div role="alert" className="history-warning">
          Este relato contém estilos ou elementos antigos que este editor não preserva integralmente.
          O original permanece no formulário até você autorizar a edição. Revise o conteúdo antes de salvar.
          <button type="button" onClick={() => setNeedsConversion(false)}>Editar com formatação básica</button>
        </div>}
        <div className="history-toolbar" role="toolbar" aria-label="Formatação do relato">
          {['BOLD', 'ITALIC', 'UNDERLINE'].map((style, index) => tool(['Negrito', 'Itálico', 'Sublinhado'][index],
            () => handleHistoricoChange(RichUtils.toggleInlineStyle(editorState, style)), editorState.getCurrentInlineStyle().has(style)))}
          {['ordered-list-item', 'unordered-list-item'].map((type, index) => tool(['Lista numerada', 'Marcadores'][index],
            () => handleHistoricoChange(RichUtils.toggleBlockType(editorState, type)), editorState.getCurrentContent().getBlockForKey(editorState.getSelection().getStartKey()).getType() === type))}
          {tool('Desfazer', () => handleHistoricoChange(EditorState.undo(editorState)), false, !editorState.getUndoStack().size)}
          {tool('Refazer', () => handleHistoricoChange(EditorState.redo(editorState)), false, !editorState.getRedoStack().size)}
        </div>

        <div
          className="bo-field history-input"
          style={{
            border: "1px solid var(--border)",
            borderRadius: "8px",
            padding: "12px",
            minHeight: "220px",
            cursor: "text",
            background: "var(--surface-raised)",
            color: "var(--text)",
          }}
          onClick={() => editorRef.current.focus()}
        >
          <Editor
            ref={editorRef}
            editorState={editorState}
            onChange={handleHistoricoChange}
            readOnly={needsConversion}
            ariaLabel="Relato da ocorrência"
            spellCheck
            handleKeyCommand={command => {
              const next = RichUtils.handleKeyCommand(editorState, command);
              if (!next) return 'not-handled';
              handleHistoricoChange(next);
              return 'handled';
            }}
          />
        </div>
        <small className="history-help">Alterações seguem o salvamento do formulário. Confira o indicador de sincronização antes de sair.</small>
      </div>

      {/* Ações */}
      <Row className="g-2 mt-4">
        <Col xs={12} sm={6} className="d-grid">
          <Button as={Link} to="../efetivo" variant="outline-secondary">
            <BsArrowLeft /> Voltar
          </Button>
        </Col>

        <Col xs={12} sm={6} className="d-grid">
          <Button variant="warning" onClick={handleSalvarHistorico}>
            Revisar <AiOutlineFileDone />
          </Button>
        </Col>

      </Row>

    </div>
  );
};

export default Historico;
